import { Card, Col, Row, Select, Skeleton } from "antd";
import { useEffect, useState } from "react";
import Chart from "react-google-charts";
import { useQuery } from "react-query";
import { pieChartConfigurationOption } from "../../config";
import HTTP from "../../services/httpService";
import CustomHeader from "../common/Header";
import CustomTable from "../common/Table";

const { Option } = Select;

const widgets = [
    { title: "Story Points", key: "story_points" },
    { title: "Open Tickets", key: "open_tickets" },
    { title: "In Progress Tickets", key: "progress_tickets" },
    { title: "Ready for Testing Tickets", key: "ready_for_qa" },
    { title: "In Testing Tickets", key: "in_qa_tickets" },
    { title: "Closed Tickets", key: "closed_tickets" },
    { title: "Total Tickets", key: "total_tickets" },
];

function SprintAnalytics() {
    const staff_id = localStorage.getItem("x-dashboard-staffid");
    const [widgetsData, setWidgetsData] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [healthData, setHealthData] = useState(null);
    const [formData, setFormData] = useState(null);
    const [sprints, setSprints] = useState([]);
    const {
        status: sprintStatus,
        data: sprintRes,
        refetch: getSprints,
    } = useQuery(
        "jira-sprints",
        async () => {
            return await HTTP("/analytics/getJiraSprints", { staff_id });
        },
        { enabled: false },
    );

    const {
        status: sprintDetailStatus,
        data: sprintDetailRes,
        refetch: getSprintDetails,
    } = useQuery(
        "sprint-details",
        async () => {
            return await HTTP("/analytics/getJiraSprintDetail", {
                ...formData,
            });
        },
        { enabled: false },
    );

    useEffect(() => {
        getSprints();
    }, []);

    useEffect(() => {
        if (formData !== null) getSprintDetails();
    }, [formData]);

    useEffect(() => {
        if (sprintStatus === "success") {
            setSprints(sprintRes.data);
            setFormData({
                staff_id,
                sprint_id: sprintRes.data.find((s) => s.state === "active")
                    .sprint_id,
            });
        }
    }, [sprintStatus, sprintRes]);

    useEffect(() => {
        if (sprintDetailStatus === "success") {
            setTableData(sprintDetailRes.data.assignees_tickets);
            setWidgetsData(
                widgets.map((w) => {
                    w.value = sprintDetailRes.data[w.key];
                    return w;
                }),
            );
            setHealthData(sprintDetailRes.data.health_gadget);
        }
    }, [sprintDetailStatus, sprintDetailRes]);

    function handleSprintSelection(sprint_id) {
        setFormData({ ...formData, sprint_id });
    }

    return (
        <Card className="dashboard-container">
            <CustomHeader />
            <div className="margin-top-20 display-flex flex-end">
                <Select
                    showSearch
                    loading={sprints.length == 0}
                    filterOption={(input, option) =>
                        option.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                    }
                    defaultValue={sprints.length > 0 && formData.sprint_id}
                    value={sprints.length > 0 && formData.sprint_id}
                    onChange={handleSprintSelection}
                >
                    {sprints.length > 0 &&
                        sprints.map((s) => (
                            <Option key={s.sprint_id} value={s.sprint_id}>
                                {s.state == "active"
                                    ? `${s.sprint_name} - CURRENT`
                                    : `${s.sprint_name}`}
                            </Option>
                        ))}
                </Select>
            </div>
            <Row>
                <Col span={18}>
                    <Card
                        className="box-shadow margin-5"
                        title="Assignee Details"
                    >
                        {sprintDetailStatus !== "idle" &&
                        sprintDetailStatus !== "loading" ? (
                            <CustomTable
                                columns={[
                                    {
                                        title: "Assignee Name",
                                        dataIndex: "assignee_name",
                                        width: "25%",
                                    },
                                    {
                                        title: "Tickets",
                                        dataIndex: "tickets",
                                    },
                                    {
                                        title: "Bugs",
                                        dataIndex: "bug_tickets",
                                    },
                                    {
                                        title: "Enhancements",
                                        dataIndex: "enhancement_tickets",
                                    },
                                    {
                                        title: "Problems",
                                        dataIndex: "problem_tickets",
                                    },
                                    {
                                        title: "Tasks",
                                        dataIndex: "task_tickets",
                                    },
                                ]}
                                tableData={tableData}
                                rowKey="assignee_name"
                                isScroll
                            />
                        ) : (
                            <Skeleton
                                active={true}
                                title={false}
                                paragraph={{ rows: 20 }}
                            />
                        )}
                    </Card>
                </Col>
                <Col span={6}>
                    <Card
                        className="box-shadow margin-5"
                        title="Ticket Details"
                    >
                        {widgetsData.length > 0 ? (
                            widgetsData.map((w) => {
                                return (
                                    <div
                                        key={w.key}
                                        className="box-shadow margin-25 border-radious-10"
                                    >
                                        <span className="flex-center purple-backgroud large-fonts">
                                            {w.title}
                                        </span>
                                        <span className="flex-center purple-fonts x-large-fonts">
                                            {w.value}
                                        </span>
                                    </div>
                                );
                            })
                        ) : (
                            <Skeleton
                                active={true}
                                title={false}
                                paragraph={{ rows: 20 }}
                            />
                        )}
                    </Card>
                </Col>
            </Row>
            <Card className="box-shadow margin-5" title="Health Analytics">
                <Row>
                    {healthData?.length > 0 ? (
                        healthData.map((data, index) => {
                            if (index > 0) {
                                return (
                                    <Col span={8} key={index}>
                                        <div className="box-shadow margin-25 border-radious-10">
                                            <span className="flex-center purple-backgroud large-fonts">
                                                {data[0]}
                                            </span>
                                            <span className="flex-center purple-fonts x-large-fonts">
                                                {data[1]}
                                            </span>
                                        </div>
                                    </Col>
                                );
                            } else {
                                return null;
                            }
                        })
                    ) : (
                        <Skeleton
                            active={true}
                            title={false}
                            paragraph={{ rows: 5 }}
                        />
                    )}
                </Row>
            </Card>
        </Card>
    );
}

export { SprintAnalytics };
